import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform, useSpring, useAnimation } from 'framer-motion';
import { useMeasure } from 'react-use';
import SplitType from 'split-type';

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  color: var(--color-text-primary);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 80% 50%, var(--color-bg-light) 0%, var(--color-bg-dark) 60%);
    opacity: 0.7;
    z-index: -1;
  }
`;

const BackgroundGradient = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    var(--color-bg-dark) 0%,
    var(--color-bg-medium) 50%,
    var(--color-bg-light) 100%
  );
  z-index: -2;
`;

const BackgroundParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
`;

const Particle = styled(motion.div)`
  position: absolute;
  background-color: var(--color-accent-primary);
  border-radius: 50%;
  opacity: 0.1;
`;

const ContentContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);

  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 120px;
  }
`;

const TextContainer = styled(motion.div)`
  width: 50%;
  padding-right: var(--spacing-md);
  
  @media (max-width: 768px) {
    width: 100%;
    padding-right: 0;
    text-align: center;
  }
`;

const Greeting = styled(motion.span)`
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 1.25rem;
  color: var(--color-accent-secondary);
  margin-bottom: var(--spacing-sm);
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin-bottom: var(--spacing-md);
  overflow: hidden;
  font-size: 3.815rem;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
  
  .char {
    display: inline-block;
    transform-origin: bottom center;
  }
`;

const SubTitle = styled(motion.p)`
  font-size: 1.5rem;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
  max-width: 500px;
  font-weight: 300;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAContainer = styled(motion.div)`
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-md);
  
  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled(motion.button)`
  background-color: var(--color-accent-primary);
  color: var(--color-bg-dark);
  border: none;
  padding: 1rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 20px rgba(166, 140, 255, 0.25);
  transition: all var(--transition-fast);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(166, 140, 255, 0.4);
    background-color: var(--color-accent-secondary);
  }
`;

const SecondaryButton = styled(motion.button)`
  background-color: transparent;
  color: var(--color-text-primary);
  border: 2px solid var(--color-accent-primary);
  padding: 1rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all var(--transition-fast);
  
  &:hover {
    transform: translateY(-2px);
    border-color: var(--color-accent-secondary);
    color: var(--color-accent-secondary);
  }
`;

const ImageContainer = styled(motion.div)`
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 40%;
    margin-top: var(--spacing-lg);
  }
`;

const ImageWrapper = styled(motion.div)`
  position: relative;
  width: 400px;
  height: 400px;
  
  @media (max-width: 768px) {
    width: 280px;
    height: 280px;
  }
`;

const ProfileImage = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 4px solid rgba(255, 255, 255, 0.1);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const GlowEffect = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 110%;
  height: 110%;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    var(--color-accent-primary) 0%,
    transparent 70%
  );
  opacity: 0.2;
  filter: blur(30px);
  z-index: -1;
`;

const ScrollPrompt = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const ScrollLine = styled(motion.div)`
  width: 1px;
  height: 60px;
  background-color: var(--color-text-secondary);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 30%;
    background-color: var(--color-accent-primary);
    animation: scrollDown 1.5s cubic-bezier(0.76, 0, 0.24, 1) infinite;
  }
  
  @keyframes scrollDown {
    0% {
      transform: translateY(0);
      opacity: 1;
    }
    90% {
      opacity: 0;
    }
    100% {
      transform: translateY(60px);
      opacity: 0;
    }
  }
`;

const ParallaxHero = () => {
  const ref = useRef(null);
  const [contentRef, { height: contentHeight }] = useMeasure();
  const { scrollY } = useScroll();
  const controls = useAnimation();
  const titleRef = useRef(null);
  
  // Parallax effect values
  const y = useTransform(scrollY, [0, contentHeight], [0, contentHeight * 0.3]);
  const textY = useTransform(scrollY, [0, contentHeight], [0, contentHeight * 0.1]);
  const imageScale = useTransform(scrollY, [0, contentHeight], [1, 0.8]);
  const imageY = useTransform(scrollY, [0, contentHeight], [0, contentHeight * 0.2]);
  const opacityImage = useTransform(scrollY, [0, contentHeight * 0.8], [1, 0]);
  const opacityContent = useTransform(scrollY, [0, contentHeight * 0.8], [1, 0]);
  
  // Spring physics for smoother motion
  const springConfig = { damping: 100, stiffness: 500, mass: 3 };
  const ySmooth = useSpring(y, springConfig);
  const textYSmooth = useSpring(textY, springConfig);
  const imageYSmooth = useSpring(imageY, { damping: 50, stiffness: 400, mass: 2 });
  const imageScaleSmooth = useSpring(imageScale, { damping: 50, stiffness: 400 });
  
  useEffect(() => {
    // Animate in the elements
    controls.start("visible");
    
    // Initialize the text splitting for characters
    if (titleRef.current) {
      const text = new SplitType(titleRef.current, { types: 'chars' });
      const chars = text.chars;
      
      if (chars) {
        chars.forEach((char, i) => {
          const element = char;
          element.style.opacity = "0";
          element.style.transform = "translateY(100%)";
          
          setTimeout(() => {
            element.style.transition = `opacity 0.3s ease ${i * 0.03}s, transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) ${i * 0.03}s`;
            element.style.opacity = "1";
            element.style.transform = "translateY(0)";
          }, 100);
        });
      }
    }
    
    // Create particles effect
    const particles = document.createElement('div');
    particles.classList.add('particles-container');
    const currentRef = ref.current;
    currentRef?.appendChild(particles);
    
    return () => {
      if (particles && currentRef?.contains(particles)) {
        currentRef.removeChild(particles);
      }
    };
  }, [controls]);
  
  // Generate random particles
  const particles = Array.from({ length: 30 }, (_, i) => {
    const size = Math.random() * 10 + 5;
    return {
      id: i,
      size,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 5,
      duration: Math.random() * 20 + 10
    };
  });
  
  return (
    <HeroSection ref={ref}>
      <BackgroundGradient 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        style={{ y: ySmooth }}
      />
      
      <BackgroundParticles>
        {particles.map((particle) => (
          <Particle
            key={particle.id}
            style={{
              width: particle.size,
              height: particle.size,
              left: `${particle.x}%`,
              top: `${particle.y}%`
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, 15, 0],
              opacity: [0.05, 0.2, 0.05]
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </BackgroundParticles>
      
      <ContentContainer ref={contentRef}>
        <TextContainer
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { duration: 0.8, staggerChildren: 0.2 } }
          }}
          style={{ y: textYSmooth, opacity: opacityContent }}
        >
          <Greeting
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
            }}
          >
            Hello, I'm
          </Greeting>
          
          <Title ref={titleRef}>
            Laura&nbsp;St.&nbsp;Pierre
          </Title>
          
          <SubTitle
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.4, ease: "easeOut" } }
            }}
          >
            School Psychologist dedicated to empowering students, supporting educators, and partnering with families throughout Colorado
          </SubTitle>
          
          <CTAContainer
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.6, ease: "easeOut" } }
            }}
          >
            <PrimaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Let's Connect
            </PrimaryButton>
            
            <SecondaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => document.getElementById('approach').scrollIntoView({ behavior: 'smooth' })}
            >
              My Approach
            </SecondaryButton>
          </CTAContainer>
        </TextContainer>
        
        <ImageContainer
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
          style={{ y: imageYSmooth, scale: imageScaleSmooth, opacity: opacityImage }}
        >
          <ImageWrapper>
            <GlowEffect
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [1, 1.1, 1]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            
            <ProfileImage>
              <img src="/images/laura-profile.jpeg" alt="Laura St. Pierre" />
            </ProfileImage>
          </ImageWrapper>
        </ImageContainer>
      </ContentContainer>
      
      <ScrollPrompt
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
      >
        <motion.span
          animate={{
            y: [0, 5, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          Scroll
        </motion.span>
        <ScrollLine />
      </ScrollPrompt>
    </HeroSection>
  );
};

export default ParallaxHero;